/**
 * Entity activity (docs/archive/OPEN_WORLD.md §4.2): the map is cut into 32 m cells, and a cell is active
 * while a player stands within `ACTIVITY.reach` cells of it (96 m). Monsters, critters and folk
 * outside the active cells think nothing: they hold their place, and the timers their thinking
 * counts down (wander, decisions, barks, a corpse's respawn) are caught up when they wake. The
 * World's own per-entity timers (cooldowns, statuses) keep running everywhere.
 *
 * The active set is a pure function of the players' positions at the end of the last step (every
 * entity of kind `player`: the World's own player, and on a server everyone else's), so the World
 * stays deterministic. Indoors (an interior map, the outside set aside) nothing is gated.
 *
 * Spawn regions: the region's wild grounds (`RegionSketch.wilds`, data/regions/<id>.ts) are set
 * down when an active cell first touches them, once per World, in the sketch's order, with the
 * World's random. On a zone's own map there are none, and no dice are drawn.
 */
import { isSolid, Tile, tileAt, type GameMap } from '../map';
import { ZONES } from '../data/zones';
import type { RegionWild } from '../region/sketch';
import { coreOf, REGIONS } from '../region/zone';
import type { Entity } from '../world';
import type { SystemHost, WorldSystem } from './types';

export const ACTIVITY = {
  /** Cell size (metres, = tiles). */
  cell: 32,
  /** Cells round a player's own that are active: 3 → a 7 × 7 block, at least 96 m every way. */
  reach: 3,
  /** Tries for an open tile per monster of a wild ground before it is left out. */
  tries: 60,
} as const;

/** The wild grounds of the region `m` is the map of (none on an authored zone map). */
function wildsOf(m: GameMap): readonly RegionWild[] {
  const id = ZONES[m.zone]?.region;
  if (!id) return [];
  const o = coreOf(m);
  if (!o.x && !o.y) return [];
  return REGIONS[id]?.sketch.wilds ?? [];
}

export class ActivitySystem implements WorldSystem {
  readonly name = 'activity';
  private map: GameMap | null = null;
  private cols = 0;
  private rows = 0;
  /** Per cell, the generation it was last marked active in; active iff equal to `gen`. */
  private stamp = new Uint32Array(0);
  private gen = 0;
  /** False indoors (and before the first pass): every entity thinks. */
  private gated = false;
  /** World time at which each sleeping entity fell asleep. */
  private since = new Map<number, number>();
  private woken = new Set<string>();
  private wilds: readonly RegionWild[] = [];
  /** Player positions of the last pass (for the spawn regions). */
  private anchors: { x: number; y: number }[] = [];

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    this.pass();
  }

  /** After everyone has moved: the active cells for the next step, and any spawn region they reach. */
  step(): void {
    this.pass();
  }

  /** How many entities are asleep now (for the benchmark and tests). */
  get sleeping(): number {
    return this.since.size;
  }

  /** Whether the 32 m cell holding (x, y) is active. */
  activeAt(x: number, y: number): boolean {
    if (!this.gated) return true;
    return this.stamp[this.cellOf(x, y)] === this.gen;
  }

  /**
   * World.step, before a monster, critter or folk thinks: true when its cell is asleep (it skips
   * this step). On waking, the timers its thinking counts down are caught up first.
   */
  asleep(e: Entity, now: number): boolean {
    if (this.activeAt(e.x, e.y)) {
      if (this.since.size) {
        const t0 = this.since.get(e.id);
        if (t0 !== undefined) {
          this.since.delete(e.id);
          catchUp(e, now - t0);
        }
      }
      return false;
    }
    if (!this.since.has(e.id)) this.since.set(e.id, now);
    return true;
  }

  private cellOf(x: number, y: number): number {
    const c = Math.min(this.cols - 1, Math.max(0, Math.floor(x / ACTIVITY.cell)));
    const r = Math.min(this.rows - 1, Math.max(0, Math.floor(y / ACTIVITY.cell)));
    return r * this.cols + c;
  }

  private pass(): void {
    const w = this.h.world;
    if (w.interiors.inside) {
      this.gated = false;
      return;
    }
    const m = w.map;
    if (m !== this.map) {
      this.map = m;
      this.cols = Math.max(1, Math.ceil(m.w / ACTIVITY.cell));
      this.rows = Math.max(1, Math.ceil(m.h / ACTIVITY.cell));
      this.stamp = new Uint32Array(this.cols * this.rows);
      this.gen = 0;
      this.wilds = wildsOf(m);
    }
    this.gated = true;
    const g = ++this.gen;
    const R = ACTIVITY.reach;
    const anchors = this.anchors;
    anchors.length = 0;
    for (const e of w.entities.values()) if (e.kind === 'player') anchors.push(e);
    if (!w.entities.has(w.player.id)) anchors.push(w.player);
    for (const p of anchors) {
      const pc = this.cellOf(p.x, p.y);
      const cx = pc % this.cols;
      const cy = (pc - cx) / this.cols;
      for (let r = Math.max(0, cy - R); r <= Math.min(this.rows - 1, cy + R); r++)
        for (let c = Math.max(0, cx - R); c <= Math.min(this.cols - 1, cx + R); c++)
          this.stamp[r * this.cols + c] = g;
    }
    // Forget sleepers that left the World (killed and removed, or carried indoors).
    if (this.since.size) for (const id of this.since.keys()) if (!w.entities.has(id)) this.since.delete(id);
    if (this.wilds.length) this.wake(m);
  }

  /** Sets down every wild ground an active cell touches for the first time. */
  private wake(m: GameMap): void {
    const S = ACTIVITY.cell;
    for (const g of this.wilds) {
      if (this.woken.has(g.id)) continue;
      const [x0, x1, y0, y1] = g.rect;
      const c0 = Math.max(0, Math.floor(x0 / S));
      const c1 = Math.min(this.cols - 1, Math.floor((x1 - 1) / S));
      const r0 = Math.max(0, Math.floor(y0 / S));
      const r1 = Math.min(this.rows - 1, Math.floor((y1 - 1) / S));
      let hit = false;
      for (let r = r0; r <= r1 && !hit; r++)
        for (let c = c0; c <= c1; c++)
          if (this.stamp[r * this.cols + c] === this.gen) {
            hit = true;
            break;
          }
      if (!hit) continue;
      this.woken.add(g.id);
      this.populate(m, g);
    }
  }

  /** Sets a ground's monsters down on open floor inside it (the World's random, in order). */
  private populate(m: GameMap, g: RegionWild): void {
    const [x0, x1, y0, y1] = g.rect;
    for (const s of g.spawns)
      for (let k = 0; k < s.n; k++)
        for (let n = 0; n < ACTIVITY.tries; n++) {
          const i = x0 + Math.floor(this.h.random() * (x1 - x0));
          const j = y0 + Math.floor(this.h.random() * (y1 - y0));
          if (tileAt(m, i, j) === Tile.Floor && !isSolid(m, i + 1, j) && !isSolid(m, i, j + 1)) {
            this.h.spawnMonster(s.monster, i + 0.5, j + 0.5);
            break;
          }
        }
  }
}

/** The timers an entity's thinking counts down, moved on by the `s` seconds it slept. */
function catchUp(e: Entity, s: number): void {
  if (!(s > 0)) return;
  if (e.wander) e.wander.t -= s;
  if (e.critter) e.critter.t -= s;
  const f = e.folk;
  if (f) {
    f.gestureT = Math.max(0, f.gestureT - s);
    if (f.gestureT <= 0) f.gesture = null;
    f.greetT = Math.max(0, f.greetT - s);
    f.barkT -= s;
    f.fleeT = Math.max(0, f.fleeT - s);
    f.scaredT = Math.max(0, f.scaredT - s);
    f.chatT -= s;
    f.blockT = Math.max(0, f.blockT - s);
  }
  const b = e.brain;
  if (b) {
    b.decisionT = Math.max(0, b.decisionT - s);
    b.blockT = Math.max(0, b.blockT - s);
    if (e.dead) e.deadT += s;
  }
}
